import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Pencil, Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { GroupAdmin } from '@tt/shared';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/combobox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Alert, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/misc';
import { Swatch } from '@/components/ui/swatch';
import {
  useAdminActivities,
  useAdminGroup,
  useAdminGroups,
  useAdminProjects,
  useAdminUsers,
  useGroupMutations,
} from '@/lib/admin-queries';
import { describeError, fieldErrorsOf } from '@/lib/errors';
import { AdminPage, ConfirmDialog, DataTable, MultiSelect, Pagination } from './admin-ui';

/** Groups list: who is in each group and how much it grants access to. */
export function AdminGroupsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ from: '/app/admin/groups' });
  const [editing, setEditing] = useState<GroupAdmin | 'new' | null>(null);
  const [deleting, setDeleting] = useState<GroupAdmin | null>(null);

  const page = search.page ?? 1;
  const groups = useAdminGroups({ q: search.q || undefined, page, pageSize: 25 });
  const mutations = useGroupMutations();

  const run = (promise: Promise<unknown>, message: string) =>
    promise.then(
      () => toast.success(message),
      (error: unknown) => toast.error(describeError(error, t)),
    );

  return (
    <AdminPage
      title={t('admin.groups.title')}
      description={t('admin.groups.subtitle')}
      actions={
        <Button onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {t('admin.groups.new')}
        </Button>
      }
    >
      <Input
        type="search"
        defaultValue={search.q ?? ''}
        onChange={(e) =>
          void navigate({
            to: '/admin/groups',
            search: { q: e.target.value || undefined, page: 1 },
            replace: true,
          })
        }
        placeholder={t('admin.groups.search')}
        aria-label={t('admin.groups.search')}
        className="max-w-xs"
      />

      <DataTable
        caption={t('admin.groups.title')}
        isPending={groups.isPending}
        error={groups.isError ? describeError(groups.error, t) : undefined}
        empty={t('admin.groups.none')}
        rows={groups.data?.data ?? []}
        rowKey={(g) => g.id}
        footer={
          <Pagination
            page={page}
            totalPages={groups.data?.meta.totalPages ?? 1}
            total={groups.data?.meta.total ?? 0}
            onChange={(next) =>
              void navigate({ to: '/admin/groups', search: { ...search, page: next } })
            }
          />
        }
        columns={[
          {
            key: 'name',
            header: t('admin.groups.name'),
            cell: (g) => (
              <Link
                to="/admin/groups/$groupId"
                params={{ groupId: g.id }}
                className="font-medium hover:underline"
              >
                {g.name}
                {g.description ? (
                  <span className="block text-xs font-normal text-muted-foreground">
                    {g.description}
                  </span>
                ) : null}
              </Link>
            ),
          },
          {
            key: 'members',
            header: t('admin.groups.members'),
            numeric: true,
            cell: (g) => g.memberCount,
          },
          {
            key: 'projects',
            header: t('admin.groups.projects'),
            numeric: true,
            cell: (g) => g.projectCount,
          },
          {
            key: 'activities',
            header: t('admin.groups.activities'),
            numeric: true,
            cell: (g) => g.activityCount,
          },
          {
            key: 'actions',
            header: <span className="sr-only">{t('admin.actions')}</span>,
            numeric: true,
            cell: (g) => (
              <div className="flex justify-end gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(g)}
                  aria-label={t('admin.groups.editName', { name: g.name })}
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setDeleting(g)}>
                  {t('app.delete')}
                </Button>
              </div>
            ),
          },
        ]}
      />

      {editing ? (
        <GroupDialog group={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : null}

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={t('admin.groups.deleteTitle', { name: deleting?.name ?? '' })}
        description={t('admin.groups.deleteBody')}
        confirmLabel={t('app.delete')}
        onConfirm={() => {
          if (!deleting) return;
          // Force: the dialog has already said what is being given up.
          void run(
            mutations.remove.mutateAsync({ id: deleting.id, force: true }),
            t('admin.groups.deleted', { name: deleting.name }),
          );
        }}
      />
    </AdminPage>
  );
}

function GroupDialog({ group, onClose }: { group: GroupAdmin | null; onClose: () => void }) {
  const { t } = useTranslation();
  const mutations = useGroupMutations();
  const [form, setForm] = useState({
    name: group?.name ?? '',
    description: group?.description ?? '',
  });
  const [error, setError] = useState<unknown>(null);
  const fieldErrors = fieldErrorsOf(error);

  const submit = async () => {
    setError(null);
    const body = { name: form.name, description: form.description || null };
    try {
      if (group) await mutations.update.mutateAsync({ id: group.id, ...body });
      else await mutations.create.mutateAsync(body);
      toast.success(
        group
          ? t('admin.groups.saved', { name: form.name })
          : t('admin.groups.created', { name: form.name }),
      );
      onClose();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {group ? t('admin.groups.editName', { name: group.name }) : t('admin.groups.new')}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label={t('admin.groups.name')} required error={fieldErrors.name}>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                required
              />
            )}
          </Field>
          <Field label={t('admin.groups.description')} error={fieldErrors.description}>
            {(props) => (
              <Input
                {...props}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              />
            )}
          </Field>
          {error && Object.keys(fieldErrors).length === 0 ? (
            <p className="text-sm text-destructive" role="alert">
              {describeError(error, t)}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('app.cancel')}
            </Button>
            <Button type="submit" disabled={!form.name.trim()}>
              {t('app.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** One group: its members, and the projects and activities it grants. */
export function AdminGroupDetailPage() {
  const { t } = useTranslation();
  const { groupId } = useParams({ from: '/app/admin/groups/$groupId' });
  const group = useAdminGroup(groupId);
  const mutations = useGroupMutations();
  const [memberQuery, setMemberQuery] = useState('');
  const users = useAdminUsers({ q: memberQuery || undefined, pageSize: 20 });
  const projects = useAdminProjects({ status: 'ACTIVE', pageSize: 200 });
  const activities = useAdminActivities({ status: 'ACTIVE', type: 'SHARED', pageSize: 200 });

  const run = (promise: Promise<unknown>, message: string) =>
    promise.then(
      () => toast.success(message),
      (error: unknown) => toast.error(describeError(error, t)),
    );

  if (group.isPending) return <Spinner />;
  if (group.isError) return <Alert variant="error">{describeError(group.error, t)}</Alert>;
  const data = group.data;
  const memberIds = new Set(data.members.map((m) => m.id));

  return (
    <AdminPage
      title={data.name}
      description={data.description ?? undefined}
      actions={
        <Link
          to="/admin/groups"
          search={{}}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {t('admin.groups.back')}
        </Link>
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('admin.groups.members')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Combobox
              value={null}
              onChange={(userId) => {
                if (!userId) return;
                void run(
                  mutations.addMembers.mutateAsync({ id: data.id, ids: [userId] }),
                  t('admin.groups.membersAdded', { count: 1 }),
                );
              }}
              items={(users.data?.data ?? [])
                .filter((u) => !memberIds.has(u.id))
                .map((u) => ({
                  value: u.id,
                  label: `${u.firstName} ${u.lastName}`,
                  hint: u.email,
                }))}
              onSearchChange={setMemberQuery}
              placeholder={t('admin.groups.addMembers')}
              aria-label={t('admin.groups.addMembers')}
            />
            <ul className="divide-y rounded-md border">
              {data.members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2 px-3 py-2">
                  <span>
                    <span className="block text-sm">
                      {m.firstName} {m.lastName}
                    </span>
                    <span className="block text-xs text-muted-foreground">{m.email}</span>
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('admin.groups.removeMember', {
                      name: `${m.firstName} ${m.lastName}`,
                    })}
                    onClick={() =>
                      void run(
                        mutations.removeMember.mutateAsync({ id: data.id, userId: m.id }),
                        t('admin.groups.memberRemoved'),
                      )
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
              {data.members.length === 0 ? (
                <li className="px-3 py-4 text-center text-sm text-muted-foreground">
                  {t('admin.groups.noMembers')}
                </li>
              ) : null}
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('admin.groups.projects')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <MultiSelect
              label={t('admin.groups.projects')}
              options={(projects.data?.data ?? []).map((p) => ({ id: p.id, label: p.name }))}
              selected={data.projects.map((p) => p.id)}
              emptyLabel={t('admin.projects.noGroups')}
              onChange={(ids) =>
                void run(
                  mutations.setProjects.mutateAsync({ id: data.id, ids }),
                  t('admin.groups.assignmentsSaved'),
                )
              }
            />
            <ul className="flex flex-wrap gap-2">
              {data.projects.map((p) => (
                <li
                  key={p.id}
                  className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs"
                >
                  <Swatch color={p.color} className="h-2 w-2" />
                  {p.name}
                </li>
              ))}
            </ul>

            <div className="border-t pt-4">
              <h3 className="mb-2 text-sm font-medium">{t('admin.groups.activities')}</h3>
              <MultiSelect
                label={t('admin.groups.activities')}
                options={(activities.data?.data ?? []).map((a) => ({ id: a.id, label: a.name }))}
                selected={data.activities.map((a) => a.id)}
                emptyLabel={t('admin.projects.noGroups')}
                onChange={(ids) =>
                  void run(
                    mutations.setActivities.mutateAsync({ id: data.id, ids }),
                    t('admin.groups.assignmentsSaved'),
                  )
                }
              />
              <ul className="mt-2 flex flex-wrap gap-2">
                {data.activities.map((a) => (
                  <li key={a.id} className="rounded-md border px-2 py-1 text-xs">
                    {a.name}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminPage>
  );
}
